// =======================================================
// Stripes — vertical bars, top-light → bottom-saturated gradient,
// per-bar intensity wave drifts left-to-right with a small vertical wobble.
// Shader-based, fills the framebuffer.
// =======================================================

import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uTime;
  uniform float uBars;
  uniform vec3  uTopColor;
  uniform vec3  uAccent;
  uniform vec3  uBg;
  uniform float uMidY;
  uniform float uHardness;
  uniform float uWaveSpeed;
  uniform float uWaveFreq;
  uniform float uWavePhase;
  uniform float uVertSpeed;
  uniform float uVertAmount;
  uniform float uBarGap;
  uniform float uGrain;
  uniform float uFloor;
  uniform float uCeil;
  uniform float uInvert;
  uniform vec2  uResolution;

  ${GLSL_COMMON}

  void main() {
    float bars = max(1.0, uBars);
    float barIdx = floor(vUv.x * bars);
    float barCenter = (barIdx + 0.5) / bars;
    float inBarU = fract(vUv.x * bars);

    // Gap between bars: render uBg in the gap.
    float gap = clamp(uBarGap, 0.0, 0.49);
    float gapMask = step(gap * 0.5, inBarU) * step(inBarU, 1.0 - gap * 0.5);

    // Left-to-right travelling wave for per-bar intensity.
    float TAU = 6.28318530718;
    float phase = barCenter * uWaveFreq * TAU - uTime * uWaveSpeed * TAU + uWavePhase;
    float waveBase = 0.5 + 0.5 * sin(phase);
    float intensity = mix(uFloor, uCeil, waveBase);

    // Per-bar vertical wobble of the gradient midpoint.
    float midY = uMidY + sin(uTime * uVertSpeed * TAU + barIdx * 0.7) * uVertAmount;

    // Vertical gradient: t=1 at top (light), t=0 at bottom (saturated).
    // uHardness controls how sharp the transition is around midY.
    float band = max(0.01, 1.0 - uHardness) * 0.5;
    float y = vUv.y;
    float t = smoothstep(midY - band, midY + band, y);

    // The "saturated" colour is the accent mixed with the top colour by intensity.
    vec3 satCol = mix(uTopColor, uAccent, intensity);
    vec3 col = mix(satCol, uTopColor, t);

    // Optional invert (light at bottom, saturated at top).
    if (uInvert > 0.5) {
      col = mix(uTopColor, satCol, t);
    }

    // Grain — static-screen / film stock feel.
    vec2 frag = vUv * uResolution;
    float n = hash21(frag * 0.5 + vec2(uTime * 11.7, uTime * 7.3));
    col += (n - 0.5) * uGrain;

    // Bar gap → background.
    col = mix(uBg, col, gapMask);

    gl_FragColor = vec4(col, 1.0);
  }
`;

export class Stripes extends Generator {
  constructor() {
    super({
      name: 'Stripes',
      category: 'ambient',
      schema: {
        bars:        { default: 128,  min: 1,  max: 512, step: 1, label: 'Bars' },
        topColor:    { default: '#f3ecea', type: 'color', label: 'Top colour' },
        accent:      { default: '#ff3a18', type: 'color', label: 'Accent' },
        bg:          { default: '#000000', type: 'color', label: 'Bar gap BG' },
        midY:        { default: 0.45, min: 0.0,  max: 1.0,  step: 0.01, label: 'Mid Y' },
        hardness:    { default: 0.0,  min: -1.0, max: 1.0,  step: 0.01, label: 'Hardness' },
        waveSpeed:   { default: 0.10, min: -2.0, max: 2.0,  step: 0.005,label: 'Wave speed (Hz)' },
        waveFreq:    { default: 1.0,  min: 0.05, max: 6.0,  step: 0.05, label: 'Wave freq' },
        floor:       { default: 0.05, min: 0.0,  max: 1.0,  step: 0.01, label: 'Intensity floor' },
        ceil:        { default: 1.0,  min: 0.0,  max: 1.0,  step: 0.01, label: 'Intensity ceil' },
        vertSpeed:   { default: 0.22, min: -3.0, max: 3.0,  step: 0.01, label: 'Vert speed (Hz)' },
        vertAmount:  { default: 0.06, min: 0.0,  max: 0.4,  step: 0.005,label: 'Vert wobble' },
        barGap:      { default: 0.0,  min: 0.0,  max: 0.4,  step: 0.005,label: 'Bar gap' },
        grain:       { default: 0.04, min: 0.0,  max: 0.3,  step: 0.005,label: 'Grain' },
        invert:      { default: false, type: 'bool', label: 'Invert vertical' },
      },
    });
    this._phase = 0;
  }

  onEnter(scene, engine) {
    this.engine = engine;
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime:       { value: 0 },
        uBars:       { value: this.v('bars') },
        uTopColor:   { value: new THREE.Color(this.v('topColor')) },
        uAccent:     { value: new THREE.Color(this.v('accent')) },
        uBg:         { value: new THREE.Color(this.v('bg')) },
        uMidY:       { value: this.v('midY') },
        uHardness:   { value: this.v('hardness') },
        uWaveSpeed:  { value: this.v('waveSpeed') },
        uWaveFreq:   { value: this.v('waveFreq') },
        uWavePhase:  { value: 0 },
        uVertSpeed:  { value: this.v('vertSpeed') },
        uVertAmount: { value: this.v('vertAmount') },
        uBarGap:     { value: this.v('barGap') },
        uGrain:      { value: this.v('grain') },
        uFloor:      { value: this.v('floor') },
        uCeil:       { value: this.v('ceil') },
        uInvert:     { value: this.v('invert') ? 1 : 0 },
        uResolution: { value: new THREE.Vector2(engine.width, engine.height) },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (!this.mesh) return;
    this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    switch (key) {
      case 'bars':       u.uBars.value = val; break;
      case 'topColor':   u.uTopColor.value.set(val); break;
      case 'accent':     u.uAccent.value.set(val); break;
      case 'bg':         u.uBg.value.set(val); break;
      case 'midY':       u.uMidY.value = val; break;
      case 'hardness':   u.uHardness.value = val; break;
      case 'waveSpeed':  u.uWaveSpeed.value = val; break;
      case 'waveFreq':   u.uWaveFreq.value = val; break;
      case 'vertSpeed':  u.uVertSpeed.value = val; break;
      case 'vertAmount': u.uVertAmount.value = val; break;
      case 'barGap':     u.uBarGap.value = val; break;
      case 'grain':      u.uGrain.value = val; break;
      case 'floor':      u.uFloor.value = val; break;
      case 'ceil':       u.uCeil.value = val; break;
      case 'invert':     u.uInvert.value = val ? 1 : 0; break;
    }
  }

  update(dt, time) {
    if (!this.mesh) return;
    this.mesh.material.uniforms.uTime.value = time;
  }

  reseed() {
    if (!this.mesh) return;
    // Shift the wave phase so the L→R sweep is offset; deterministic from seed.
    this.mesh.material.uniforms.uWavePhase.value = this.rand01(1) * Math.PI * 2;
  }
}
